import useAnalytics from "@hooks/useAnalytics";
import useClientContext from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import { useEffect } from "react";
import { FONTS, media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled, { css } from "styled-components";
import DraftKingsLogo from "../components/svgs/DraftKings-color.svg";
import DriverModalBannerAd from "./ads/DriverModalBannerAd";
import DriverModalTopAd from "./ads/DriverModalTopAd";
import Modal, { playoffsModalCSS } from "./Modal";

function DriverModal() {
  const [{ openModal }] = useClientContext();
  const { data: game } = useGame();

  const driverId = openModal.options?.driverId;
  const driver = game?.drivers?.find((driver) => driver.driver_id === driverId) ?? null;
  const current_round = game?.state?.current_round;
  const odds = driver?.odds[current_round - 1];
  const oddsText = odds ? `${odds > 0 ? `+${odds}` : odds}` : ` - `;
  const shouldRenderModal = driver && openModal.name === "DRIVER_MODAL";
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (shouldRenderModal) trackEvent("picks:driver_modal");
  }, [trackEvent, shouldRenderModal]);

  return (
    shouldRenderModal && (
      <Modal aria-label="Driver" css={modalCSS}>
        <ModalContainer>
          <HeaderContainer>
            <CarDetails>
              <Number>
                <img src={driver.badge} alt="Driver Number" height="38" />
              </Number>
              <Manufacturer>
                <img
                  src={`${publicBasePath}/images/${driver.manufacturer}.png`}
                  alt="Driver Manufacturer"
                  height="30"
                />
              </Manufacturer>
            </CarDetails>
            <DriverHeadshot>
              <img src={driver.headshot} alt="Driver Headshot" height="171" />
            </DriverHeadshot>
            <DriverModalTopAd />
          </HeaderContainer>

          <div>
            <BorderContainer>
              <DriverName>{driver.full_name}</DriverName>
              <OddsContainer>
                <OddsSponsor
                  href="https://sharplink.routy.app/68800?ts=5001955&pa=Playoff_Drivers_Modal"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  <div className="presentedBy">Odds Presented By</div>
                  <DraftKingsLogo className="sponsor-logo" />
                </OddsSponsor>
                <div className="header"> Championship Odds: {oddsText}</div>
              </OddsContainer>
              <BioContainer>
                <BioSubsection>
                  <div className="header">Team</div>
                  <div className="info">{driver.team_name}</div>
                </BioSubsection>
                <BioSubsection className="crewChief">
                  <div className="header">Crew Chief</div>
                  <div className="info">{driver.crew_chief || "Not Available"}</div>
                </BioSubsection>
                <BioSubsection>
                  <div className="header">Hometown</div>
                  <div className="info">{driver.hometown}</div>
                </BioSubsection>
              </BioContainer>

              {driver.track_stats.map((track, i) => (
                <TrackStats key={`${i}${track.track_name}`}>
                  <span className="trackName">{track.track_name} Track Stats</span>
                  <div className="headerStats">
                    <div className="header">starts</div>
                    <div className="header">poles</div>
                    <div className="header">top 10</div>
                    <div className="header">top 5</div>
                    <div className="header">wins</div>
                  </div>

                  <div className="statsContainer">
                    <span className="stat">{track.starts}</span>
                    <span className="stat">{track.poles}</span>
                    <span className="stat">{track.top10}</span>
                    <span className="stat">{track.top5}</span>
                    <span className="stat">{track.wins}</span>
                  </div>
                </TrackStats>
              ))}
            </BorderContainer>
            <FooterAd>
              <DriverModalBannerAd />
            </FooterAd>
          </div>
        </ModalContainer>
      </Modal>
    )
  );
}

export default DriverModal;

const modalCSS = css`
  ${playoffsModalCSS}

  .closeIcon {
    color: black;
  }
`;

const ModalContainer = styled.div`
  background-color: ${({ theme }) => theme.grey_700};
  padding-bottom: ${pxToRem(4)};
`;

const HeaderContainer = styled.div`
  justify-content: space-between;
  align-items: flex-end;
  display: flex;
  width: 100%;
  height: ${pxToRem(134)};
  background-color: white;

  ${media.mobileMid} {
    min-height: ${pxToRem(114)};
  }
`;
const DriverHeadshot = styled.div`
  position: relative;
  height: ${pxToRem(171)};
  img {
    height: 100%;
  }
  ${media.mobileMid} {
    img {
      min-height: ${pxToRem(141)};
    }
  }
`;
const CarDetails = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  ${media.mobileMid} {
    margin-left: ${pxToRem(10)};
  }
`;
const Manufacturer = styled.div`
  margin-left: ${pxToRem(6)};
  height: ${pxToRem(30)};
  width: ${pxToRem(57)};
  ${media.mobileMid} {
    margin-left: ${pxToRem(3)};
    img {
      height: ${pxToRem(20)};
    }
  }
`;
const Number = styled.div`
  color: white;
  img {
    height: ${pxToRem(38)};
  }
  ${media.mobileMid} {
    img {
      height: ${pxToRem(28)};
    }
  }
`;

const DriverName = styled.div`
  background-color: ${({ theme }) => theme.grey_700};
  color: ${({ theme }) => theme.gold};
  font-size: ${pxToRem(36)};
  font-family: ${FONTS.PRIMARY};
  font-weight: bold;
  text-align: center;
  letter-spacing: 0px;
  line-height: ${pxToRem(36)};
  position: relative;
  top: ${pxToRem(-18)};
  margin: 0 auto;
  padding: 0 ${pxToRem(12)} 0 ${pxToRem(5)};
  text-transform: uppercase;

  ${media.mobile} {
    font-size: ${pxToRem(30)};
    line-height: ${pxToRem(30)};
    top: ${pxToRem(-15)};
  }
`;
const OddsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${pxToRem(14)};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0px;
  line-height: ${pxToRem(14)};

  .header {
    height: ${pxToRem(14)};
    color: white;
    font-size: ${pxToRem(14)};
    font-weight: 900;
    text-align: right;
    text-transform: uppercase;
    letter-spacing: 0px;
    line-height: ${pxToRem(14)};
  }

  svg {
    width: ${pxToRem(80)};
    height: ${pxToRem(15)};
  }

  .presentedBy {
    font-size: ${pxToRem(9)};
    font-weight: normal;
    font-style: italic;
    letter-spacing: 0.3px;
    text-transform: none;
    color: #fff;
  }
`;
const OddsSponsor = styled.a`
  margin-right: ${pxToRem(15)};
  text-decoration: none;
  .sponsor-logo {
    height: 32.5px;
    width: 92px;
    position: relative;
    left: -8px;
  }
`;
const BioContainer = styled.div`
  margin: ${pxToRem(20)} 0;
  padding: ${pxToRem(16.5)} 0;
  display: flex;
  justify-content: flex-start;
  min-height: ${pxToRem(65)};
  background: ${({ theme }) => theme.grey_blue};
  border-radius: 0px;
  .header {
    margin-bottom: ${pxToRem(3)};
    color: ${({ theme }) => theme.grey_150};
    font-size: ${pxToRem(10)};
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0px;
  }

  .info {
    color: white;
    font-size: ${pxToRem(14)};
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0px;
    line-height: ${pxToRem(14)};
    padding-right: ${pxToRem(10)};
  }
`;
const TrackStats = styled.div`
  color: white;
  margin-bottom: ${pxToRem(10)};

  .headerStats {
    display: flex;
    justify-content: space-around;
    padding-top: ${pxToRem(15)};
  }

  .statsContainer {
    display: flex;
    justify-content: space-around;
  }

  .trackName {
    width: ${pxToRem(163)};
    height: ${pxToRem(22)};
    color: ${({ theme }) => theme.gold};
    font-size: ${pxToRem(20)};
    font-family: ${FONTS.SECONDARY};
    font-weight: normal;
    letter-spacing: 0px;
    line-height: ${pxToRem(22)};
    padding-left: ${pxToRem(34)};

    ${media.mobile} {
      padding-left: ${pxToRem(16)};
    }
  }

  .header {
    height: ${pxToRem(14)};
    color: ${({ theme }) => theme.grey_150};
    font-size: ${pxToRem(10)};
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0px;
  }

  .stat {
    height: ${pxToRem(25)};
    color: white;
    font-size: ${pxToRem(18)};
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0px;
  }
`;
const FooterAd = styled.div`
  margin: 0 ${pxToRem(8)} ${pxToRem(28)};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BioSubsection = styled.div`
  &.crewChief {
    ${media.mobile} {
      display: none;
    }
  }

  &:first-child {
    padding-left: ${pxToRem(32)};
  }
  &:last-child {
    padding-right: ${pxToRem(32)};
  }

  ${media.mobile} {
    padding: 0 ${pxToRem(8)};
    &:first-child {
      padding-left: ${pxToRem(16)};
    }
    &:last-child {
      padding-right: ${pxToRem(16)};
    }
  }
`;

const BorderContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: ${pxToRem(3)} solid ${({ theme }) => theme.gold};
  margin: ${pxToRem(24)} ${pxToRem(24)} ${pxToRem(24)} ${pxToRem(24)};
`;
